import React, { Children, useEffect, useMemo } from 'react';
import styled, { x } from '@xstyled/styled-components';
import DOMPurify from 'dompurify';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { TABS, TEXT } from '../itemIds';
import { IconButton } from '~/training/sequence/templates/items/carousel/components/IconButton';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import audioEffect from '~/assets/mp3/card_flip.mp3';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { t } from 'ttag';
import { useEditorStore } from '~/shell/Store';
import { DuplicateTabButton } from '~/training/sequence/templates/items/tab/DuplicateTabButton';
import { TabOptionsButton } from '~/training/sequence/templates/items/tab/TabOptionsButton';
import {
  Text,
  TextDefinition
} from '~/training/sequence/templates/items/text/Text';
import { DragHandle } from '~/training/sequence/templates/items/tab/DragHandle';
import { useReorderTabs } from '~/training/sequence/templates/items/tab/useReorderTabs';
import type { TabItem } from '~/training/sequence/templates/items/tab/types';

const DEFAULT_MAX_TABS = 4;

interface Props {
  value?: string;
  tabsTitles?: string[];
  maxTabs?: number;
}

export type TabsDefinition = ItemDefinition<typeof TABS, Props>;

// TODO #F3ECFF move to var
const TabsNav = styled.ul`
  width: 80%;
  margin-bottom: -15px;
  z-index: 1;
  & > li {
    float: left;
    padding: 20px;
    padding-top: 10px;
    cursor: pointer;
    min-width: 200px;
    background: #f3ecff;
    box-shadow: 0px -2px 4px rgba(0, 0, 0, 0.05),
      0px 30px 30px rgba(0, 0, 0, 0.03), 0px 15px 20px rgba(0, 0, 0, 0.05);
    border-radius: 20px;
    font-family: Recoleta;
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    line-height: 150%;
    text-transform: capitalize;
    color: #000000;
  }
  .active {
    background: ${p => p.theme.colors.white};
    color: ${p => p.theme.colors.black};
  }

  @media (max-width: md) {
    display: flex;
    & > li {
      min-width: 100px;
      width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 13px;
    }
  }

  @media (max-width: sm) {
    display: none;
  }
`;

const TabsBox = styled.div`
  background-color: white;
  width: 80%;
  height: 75vh;
  overflow: hidden;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  border-top-right-radius: 20px;
  z-index: 2;

  & > .content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    align-content: center;
  }
  .tabContainer {
    height: 100%;
    border-top-right-radius: 20px;
  }
  .tabContent {
    display: contents;
    > div:first-child {
      padding: 16px;
      overflow: auto;
    }
  }
`;

const DuplicateButtonWrapper = styled.li`
  float: left;
  padding: 0 !important;
  min-width: auto !important;
  width: auto !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
`;

const DuplicateTabButtonModified = styled(DuplicateTabButton)`
  padding: 13px;
`;

const TabContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-right: -8px;
`;

const TabTitleWrapper = styled.div`
  margin: -2px;
  flex: 1;
`;

const TabDeleteWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TabTitleText: React.FC<{ id: string; value: string }> = ({
  id,
  value
}) => {
  const TextComponent = useMemo(() => {
    const textDefinition: TextDefinition = {
      type: TEXT,
      id,
      values: {
        value,
        variant: 'text',
        editable: true,
        maxCharacters: 20,
        allowNewLines: false,
        disableRewrite: true
      },
      items: []
    };

    const textItem = new Text(textDefinition);
    return textItem.component();
  }, [id]);

  return <TextComponent />;
};

const TabContent: React.FC<{
  childArray: React.ReactNode[];
  editorTabItems: TabItem[] | null;
  activeTab: number;
}> = ({ childArray, editorTabItems, activeTab }) => {
  const memoizedContents = useMemo(() => {
    return childArray.map((c, i) => {
      const tab = editorTabItems?.[i];

      return {
        id: tab?.id ?? i,
        content: (
          <x.div key={`tabContent_${tab?.id ?? i}`} className="tabContent">
            {c}
          </x.div>
        )
      };
    });
  }, [childArray, editorTabItems?.map(t => t.id).join(',')]);

  return (
    <>
      {memoizedContents.map((item, i) => (
        <x.div
          key={item.id}
          style={{ display: activeTab === i ? 'contents' : 'none' }}
        >
          {item.content}
        </x.div>
      ))}
    </>
  );
};

export class Tabs extends Item<typeof TABS, Props> {
  protected render({
    children,
    tabsTitles,
    maxTabs = DEFAULT_MAX_TABS
  }: RenderProps<Props>) {
    const { contentEditable, sequence } = useEditorStore();
    const sequenceId = sequence?.id?.toString() || 'default';
    const { getActiveTab, setActiveTab: setActiveTabInStore } =
      useSequenceStore();

    const activeTab = getActiveTab(sequenceId);
    const setActiveTab = (index: number) => {
      setActiveTabInStore(sequenceId, index);
    };

    useEffect(() => {
      const currentLength = tabsTitles?.length || 0;

      if (activeTab >= currentLength && currentLength > 0) {
        setActiveTab(currentLength - 1);
      }
    }, [tabsTitles?.length]);

    const childArray = Children.toArray(children);

    const { addBlocker, removeBlocker } = useSequenceStore();
    const variant = 'horizontal';
    const index = activeTab;
    const [playAudioEffect] = useAudioEffect(audioEffect);

    useGlobalMedia();

    const {
      onDragStart,
      onDragEnter,
      onDrop,
      onDragOver,
      onDragEnd,
      onPointerDown
    } = useReorderTabs();

    const globalMedia = useGlobalMediaStore();
    const globalPlayer = globalMedia?.player;

    const prev = () => {
      if (globalPlayer) {
        try {
          globalPlayer.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
      }

      setTimeout(() => {
        if (!contentEditable) {
          try {
            playAudioEffect();
          } catch (error) {
            // Silently ignore audio effect errors
          }
        }
        setActiveTab(activeTab - 1);
      }, 0);
    };

    const next = () => {
      if (globalPlayer) {
        try {
          globalPlayer.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
      }

      setTimeout(() => {
        if (!contentEditable) {
          try {
            playAudioEffect();
          } catch (error) {
            // Silently ignore audio effect errors
          }
        }
        setActiveTab(activeTab + 1);
        removeBlocker(`tab_${activeTab + 1}`);
      }, 0);
    };

    const clickOnTab = (i: number) => {
      if (globalPlayer) {
        try {
          globalPlayer.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
      }

      setTimeout(() => {
        if (!contentEditable) {
          try {
            playAudioEffect();
          } catch (error) {
            // Silently ignore audio effect errors
          }
        }
        setActiveTab(i);
        removeBlocker(`tab_${i}`);
      }, 0);
    };

    useEffect(() => {
      for (let i = 1; i < childArray.length; i++) {
        addBlocker(`tab_${i}`);
      }
    }, []);

    const editorTabItems: TabItem[] | null = contentEditable
      ? (sequence?.items?.filter(
          (it: TabItem) => it.__typename === 'Tab' || it.typename === 'Tab'
        ) as TabItem[]) ?? []
      : null;

    const tabCount = editorTabItems?.length ?? 0;
    const canAddTab = tabCount < maxTabs;

    return (
      <>
        <IconButton
          key="prev"
          onClick={prev}
          gridArea="prev"
          aria-label={t`Previous`}
          transform
          rotate={variant === 'horizontal' ? '180deg' : '-90deg'}
          justifySelf={variant === 'horizontal' ? 'end' : 'start'}
          alignSelf={variant === 'horizontal' ? 'center' : 'end'}
          disabled={index === 0}
          position="absolute"
          left="3%"
          tabIndex={0}
        />
        <TabsNav className="tabNav" role="tablist">
          {(tabsTitles ?? []).map((title: string, i: number) => {
            const tab = editorTabItems?.[i];
            const tabTitle = title;
            const tabId = tab ? `${tab.id}_title` : `tab_${i}_title`;
            const sanitizedTabTitle = DOMPurify.sanitize(tabTitle, {
              USE_PROFILES: { html: true }
            });

            const liProps = {
              'aria-selected': i == activeTab,
              role: 'tab' as const,
              key: contentEditable && tab ? `tab_${tab.id}` : `tabId_${i}`,
              className: i == activeTab ? 'tabBtn active' : 'tabBtn',
              onClick: () => clickOnTab(i),
              onKeyPress: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  clickOnTab(i);
                }
              },
              tabIndex: 0
            };

            return contentEditable ? (
              <x.li
                {...liProps}
                data-tab-id={tab?.id}
                draggable={contentEditable}
                onDragStart={
                  tab ? e => onDragStart(String(tab.id), e) : undefined
                }
                onDrop={tab ? () => onDrop() : undefined}
                onDragOver={tab ? onDragOver : undefined}
                onDragEnter={
                  tab ? () => onDragEnter(String(tab.id)) : undefined
                }
                onDragEnd={tab ? onDragEnd : undefined}
                onPointerDown={
                  tab
                    ? e => {
                        const target = e.target as HTMLElement;
                        if (target.closest('[data-drag-handle]')) {
                          onPointerDown(String(tab.id), e);
                        }
                      }
                    : undefined
                }
              >
                <TabContentWrapper>
                  {tab && <DragHandle />}
                  <TabTitleWrapper onClick={e => e.stopPropagation()}>
                    <TabTitleText id={tabId} value={tabTitle} />
                  </TabTitleWrapper>
                  {tab && (
                    <TabDeleteWrapper onClick={e => e.stopPropagation()}>
                      <TabOptionsButton
                        tabId={tab.id}
                        tabItems={editorTabItems}
                        maxTabs={maxTabs}
                      />
                    </TabDeleteWrapper>
                  )}
                </TabContentWrapper>
              </x.li>
            ) : (
              <x.li
                {...liProps}
                dangerouslySetInnerHTML={{ __html: sanitizedTabTitle }}
              />
            );
          })}
          {contentEditable && canAddTab && (
            <DuplicateButtonWrapper key="duplicate-tab-button">
              <DuplicateTabButtonModified tabItems={editorTabItems} />
            </DuplicateButtonWrapper>
          )}
        </TabsNav>
        <TabsBox>
          <x.div className="tabContainer">
            {contentEditable ? (
              <TabContent
                childArray={childArray}
                editorTabItems={editorTabItems}
                activeTab={activeTab}
              />
            ) : (
              childArray.map((c, i) => {
                if (activeTab != i) return null;

                return (
                  <x.div key={`tabContent_${i}`} className="tabContent">
                    {c}
                  </x.div>
                );
              })
            )}
          </x.div>
        </TabsBox>
        <IconButton
          key="next"
          onClick={next}
          gridArea="next"
          aria-label={t`Next`}
          transform
          rotate={variant === 'horizontal' ? '0deg' : '90deg'}
          justifySelf="start"
          alignSelf={variant === 'horizontal' ? 'center' : 'start'}
          disabled={index === childArray.length - 1}
          position="absolute"
          right="3%"
          tabIndex={0}
        />
      </>
    );
  }
}

registry[TABS] = Tabs;
